import { cableIsNegativePath, isFuseHolderVariant } from "../auto-fuse";
import { getEffectiveCableFuse } from "../cable-current";
import { computeSystemCableSpec } from "../cable-spec";
import { getDefinition } from "../catalog";
import { effectiveTerminals } from "../effective";
import { BATTERY_TERMINAL_FUSE_TYPES } from "../fuse-aic";
import { autoFuseNeedsReview } from "../terminal-fuse-limit";
import type {
  Cable,
  ComponentNode,
  FuseType,
  System,
  SystemNode,
  Terminal,
} from "../types";

/**
 * No fuse on a negative or ground leg — ABYC E-11 puts overcurrent protection on
 * the positive conductor.
 *
 * Lives here rather than in `system-draft.ts`: the Pages Function bundles that
 * module and the sizing engine is not edge-safe.
 */
export function fuseGeneratedSystem(system: System, only?: ReadonlySet<string>, autoSizeTargetPct?: number): System {
  const nodesById: Record<string, SystemNode> = Object.fromEntries(
    system.nodes.map((n) => [n.id, n]),
  );

  const cables: Cable[] = system.cables.map((cable) => {
    // A cable the user drew and left unfused must not acquire one behind their back.
    if (only && !only.has(cable.id)) return cable;
    const from = terminalOf(nodesById[cable.fromNodeId], cable.fromTerminalId);
    const to = terminalOf(nodesById[cable.toNodeId], cable.toTerminalId);
    if (cableIsNegativePath(from, to)) return cable;
    // A supplied declaration is not a new engine choice. Keep its value and
    // provenance, including conflicts with a holder, for the diagnostics.
    if (cable.fuseAmpOverride != null) return cable;

    const rating = holderRating(nodesById, cable);
    if (rating != null) {
      const { fuseAmpAuto: _auto, ...rest } = cable;
      return { ...rest, fuseAmpOverride: rating };
    }

    // A cable onto a battery+ carrying its own MRBF/MEGA already reports that
    // rating through inheritance; rating it here claims a second fuse.
    if (
      getEffectiveCableFuse(cable, nodesById, getDefinition).source.rating ===
      "battery"
    ) {
      return cable;
    }

    const spec = computeSystemCableSpec(cable, system, getDefinition, undefined, autoSizeTargetPct);
    // An AC run (shore L/N/PE, generator, inverter AC-out) is protected by the
    // inlet's breaker, never by a fuse the planner picks.
    if (spec.isAc || spec.sizingVerificationRequired || spec.fuseRecommendationUnavailableReason) return cable;
    // 0 is "nothing to rate against" — a run whose ends resolve no current.
    const fuse = spec.fuse;
    return fuse > 0 ? { ...cable, fuseAmpOverride: fuse, fuseAmpAuto: true } : cable;
  });

  return promoteBatteryTerminalFuses({ ...system, cables }, only);
}

const POST_MOUNT_FAMILIES = new Set<FuseType>(BATTERY_TERMINAL_FUSE_TYPES);

/**
 * "A fuse somewhere on this cable" and "an MRBF on the post" are different facts
 * to the detectors: E-11.10 assumes an unplaced cable fuse sits at the far end,
 * and `battery-missing-terminal-fuse` only ever reads `batteryFuse`.
 *
 * The cable gives up its rating and family — keeping both describes two fuses in
 * series where the installer fits one.
 */
function promoteBatteryTerminalFuses(
  system: System,
  only?: ReadonlySet<string>,
): System {
  type Promotion = { cableId: string; nodeId: string; type: FuseType; ratingA: number };

  const nodesById: Record<string, SystemNode> = Object.fromEntries(
    system.nodes.map((n) => [n.id, n]),
  );

  const candidates: Promotion[] = [];
  for (const cable of system.cables) {
    if (only && !only.has(cable.id)) continue;
    const type = cable.fuseTypeOverride;
    const ratingA = cable.fuseAmpOverride;
    if (!type || !POST_MOUNT_FAMILIES.has(type) || ratingA == null) continue;
    if (cable.fuseAmpAuto) {
      const spec = computeSystemCableSpec(cable, system, getDefinition);
      if (spec.sizingVerificationRequired || spec.manufacturerInstallationUnverified || spec.fuseRecommendationUnavailableReason || autoFuseNeedsReview(cable, spec)) continue;
    }
    for (const [nodeId, terminalId] of [
      [cable.fromNodeId, cable.fromTerminalId],
      [cable.toNodeId, cable.toTerminalId],
    ] as const) {
      const node = nodesById[nodeId];
      if (!node || node.kind !== "component") continue;
      const def = getDefinition(node.definitionId, node.customDefinition);
      if (def?.category !== "battery") continue;
      if (terminalOf(node, terminalId)?.polarity !== "positive") continue;
      candidates.push({ cableId: cable.id, nodeId, type, ratingA });
      break;
    }
  }
  if (candidates.length === 0) return system;

  // `batteryFuse` holds one per battery: the largest run claims it, the rest
  // keep their cable-level fuse.
  const winners = new Map<string, Promotion>();
  for (const c of candidates) {
    const best = winners.get(c.nodeId);
    if (!best || c.ratingA > best.ratingA) winners.set(c.nodeId, c);
  }

  const promotedCableIds = new Set<string>();
  const nodes = system.nodes.map((node) => {
    const win = winners.get(node.id);
    if (!win || node.kind !== "component" || node.batteryFuse) return node;
    promotedCableIds.add(win.cableId);
    return { ...node, batteryFuse: { ratingA: win.ratingA, type: win.type } };
  });
  if (promotedCableIds.size === 0) return system;

  const cables = system.cables.map((cable) => {
    if (!promotedCableIds.has(cable.id)) return cable;
    const { fuseAmpOverride: _rating, fuseTypeOverride: _type, fuseAmpAuto: _auto, ...rest } = cable;
    return rest;
  });

  return { ...system, nodes, cables };
}

function holderRating(
  nodesById: Record<string, SystemNode>,
  cable: Cable,
): number | null {
  for (const [nodeId, terminalId] of [
    [cable.fromNodeId, cable.fromTerminalId],
    [cable.toNodeId, cable.toTerminalId],
  ] as const) {
    const node = nodesById[nodeId];
    if (!node || node.kind !== "component") continue;
    const def = getDefinition(node.definitionId, node.customDefinition);
    if (!isFuseHolderVariant(def)) continue;
    const terminal = terminalOf(node, terminalId);
    if (terminal?.direction === "output" && node.fuseAmp) return node.fuseAmp;
  }
  return null;
}

function terminalOf(
  node: SystemNode | undefined,
  terminalId: string,
): Terminal | undefined {
  if (!node || node.kind !== "component") return undefined;
  const component = node as ComponentNode;
  const def = getDefinition(component.definitionId, component.customDefinition);
  if (!def) return undefined;
  // `effectiveTerminals`, not `def.terminals`: busbar studs are synthesized from
  // `studCount` and are absent from the catalog entry.
  return effectiveTerminals(def, component).find((t) => t.id === terminalId);
}
